import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  useTheme,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { COMPANY_INFORMATION_PATH } from '@/utils/Constant';

interface AiNotConfiguredDialogProps {
  open: boolean;
  onClose: () => void;
}

const AiNotConfiguredDialog: React.FC<AiNotConfiguredDialogProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const handleGoToSettings = () => {
    onClose();
    navigate(COMPANY_INFORMATION_PATH);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          padding: 1,
          backgroundColor: theme.palette.background.paper,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingBottom: 1,
          fontSize: '16px',
          fontWeight: 600,
          color: theme.palette.text.primary,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(245, 158, 11, 0.12)',
            }}
          >
            <AutoFixHighIcon
              sx={{ fontSize: 20, color: '#f59e0b' }}
            />
          </Box>
          {t("AI Not Configured")}
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ paddingTop: 1, paddingBottom: 2 }}>
        <DialogContentText
          sx={{
            fontSize: '14px',
            color: theme.palette.text.primary,
            lineHeight: 1.5,
          }}
        >
          {t("No AI provider is currently active. To use AI-powered features, please configure your AI provider in Company Settings.")}
        </DialogContentText>
      </DialogContent>

      <DialogActions sx={{ padding: 2, paddingTop: 0, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            minWidth: 80,
            textTransform: 'none',
            borderColor: theme.palette.divider,
            color: theme.palette.text.primary,
            '&:hover': {
              borderColor: theme.palette.primary.main,
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          {t("Close")}
        </Button>
        <Button
          onClick={handleGoToSettings}
          variant="contained"
          sx={{
            minWidth: 80,
            textTransform: 'none',
          }}
        >
          {t("Go to Settings")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AiNotConfiguredDialog;
